import React, { useState, useMemo, useEffect } from "react";
import {
    Modal,
    Box,
    Flex,
    Stack,
    Text,
    Button,
    Checkbox,
    Table,
    ScrollArea,
    Tabs,
    ThemeIcon,
    Loader,
    useMantineTheme,
    Badge,
    Switch,
} from "@mantine/core";
import { MaterialSymbol } from "react-material-symbols";
import { useMediaQuery } from "@mantine/hooks";
import { ArticleDetails } from "../../../../../interfaces/response/ArticleDetails";
import { useLazyGetBackupArticleQuery } from "../../../../../services/ArticleService";
import { 
    mapArticleAttributes, 
    articleAttributes, 
    getAttributesByTab, 
    COMPARE_TAB_CONFIGS,
    CompareTabKey 
} from "../../../../../utils/ArticleUtils";
import classes from "./CompareModal.module.css";

interface CompareModalProps {
    opened: boolean;
    onClose: () => void;
    currentArticle: ArticleDetails;
    onConfirm: (selectedFields: Record<string, any>) => void;
}

interface FieldComparison {
    key: string;
    label: string;
    currentValue: any;
    archivedValue: any;
    selected: boolean;
    isDifferent: boolean;
}

export default function CompareModal({ opened, onClose, currentArticle, onConfirm }: CompareModalProps) {
    const theme = useMantineTheme();
    const isMobile = useMediaQuery("(max-width: 48em)");
    
    const [activeTab, setActiveTab] = useState<CompareTabKey>("articleInfo");
    const [archivedArticle, setArchivedArticle] = useState<ArticleDetails | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [fieldSelections, setFieldSelections] = useState<Record<string, boolean>>({});
    
    const [fetchBackupArticle] = useLazyGetBackupArticleQuery();

    useEffect(() => {
        if (opened && currentArticle) {
            setIsLoading(true);
            setError(null);
            
            fetchBackupArticle(currentArticle.headerArticleDetails.code)
                .unwrap()
                .then((data) => {
                    setArchivedArticle(data);
                    setIsLoading(false);
                })
                .catch((err) => {
                    console.error("Error fetching backup article:", err);
                    setError("Errore nel recupero dei dati dall'archivio");
                    setIsLoading(false);
                });
        }
    }, [opened, currentArticle, fetchBackupArticle]);

    const currentAttributes = useMemo(() => {
        return mapArticleAttributes(currentArticle);
    }, [currentArticle]);

    const archivedAttributes = useMemo(() => {
        if (!archivedArticle) return {};
        return mapArticleAttributes(archivedArticle);
    }, [archivedArticle]);

    const attributesByTab = useMemo(() => {
        return getAttributesByTab(currentArticle.headerArticleDetails.category);
    }, [currentArticle.headerArticleDetails.category]);

    const comparisons = useMemo(() => {
        const result: Record<CompareTabKey, FieldComparison[]> = {
            articleInfo: [],
            technicalDetails: [],
            description: [],
            otherDetails: [],
        };

        COMPARE_TAB_CONFIGS.forEach((tabConfig) => {
            const fieldsForTab = attributesByTab[tabConfig.key];
            
            fieldsForTab.forEach((fieldKey) => {
                const attrConfig = Object.entries(articleAttributes).find(
                    ([key, attr]) => key === fieldKey || attr.formLabel === fieldKey
                );
                
                const formLabel = attrConfig?.[1]?.formLabel || attrConfig?.[0] || fieldKey;
                const displayLabel = attrConfig?.[1]?.label || attrConfig?.[0] || fieldKey;
                
                const currentVal = currentAttributes[formLabel];
                const archivedVal = archivedAttributes[formLabel];

                if (currentVal !== undefined || archivedVal !== undefined) {
                    const isDifferent = String(currentVal || "") !== String(archivedVal || "");
                    
                    result[tabConfig.key].push({
                        key: formLabel,
                        label: displayLabel,
                        currentValue: currentVal,
                        archivedValue: archivedVal,
                        selected: fieldSelections[formLabel] ?? true,
                        isDifferent,
                    });
                }
            });
        });

        return result;
    }, [currentAttributes, archivedAttributes, fieldSelections, attributesByTab]);

    useEffect(() => {
        if (archivedArticle && Object.keys(fieldSelections).length === 0) {
            const initialSelections: Record<string, boolean> = {};
            Object.values(comparisons).flat().forEach((comp) => {
                initialSelections[comp.key] = true;
            });
            setFieldSelections(initialSelections);
        }
    }, [archivedArticle, comparisons, fieldSelections]);

    const handleFieldToggle = (fieldKey: string) => {
        setFieldSelections((prev) => ({
            ...prev,
            [fieldKey]: !prev[fieldKey],
        }));
    };

    const handleSelectAllInTab = (tabKey: CompareTabKey, selected: boolean) => {
        const tabFields = comparisons[tabKey];
        setFieldSelections((prev) => {
            const newSelections = { ...prev };
            tabFields.forEach((field) => {
                newSelections[field.key] = selected;
            });
            return newSelections;
        });
    };

    const handleConfirm = () => {
        const selectedFields: Record<string, any> = {};
        Object.values(comparisons).flat().forEach((comp) => {
            if (fieldSelections[comp.key]) {
                selectedFields[comp.key] = comp.archivedValue;
            }
        });
        onConfirm(selectedFields);
        onClose();
    };

    const getTabDiffCount = (tabKey: CompareTabKey): number => {
        return comparisons[tabKey].filter((f) => f.isDifferent).length;
    };

    const getSelectedCount = (): number => {
        return Object.values(fieldSelections).filter(Boolean).length;
    };

    const renderHtmlComparison = (fields: FieldComparison[]) => {
        if (fields.length === 0) {
            return (
                <Flex justify="center" align="center" p="xl">
                    <Text c="dimmed">Nessun campo disponibile per questa sezione</Text>
                </Flex>
            );
        }

        const field = fields[0];
        const currentHtml = field.currentValue || "";
        const archivedHtml = field.archivedValue || "";

        return (
            <Stack gap="md" h="100%">
                <Flex justify="space-between" align="center">
                    <Flex align="center" gap="sm">
                        <Switch
                            checked={fieldSelections[field.key] ?? true}
                            onChange={() => handleFieldToggle(field.key)}
                            label="Importa da archivio"
                            color="teal"
                        />
                        {field.isDifferent && (
                            <Badge size="sm" color="orange" variant="light">
                                Contenuto diverso
                            </Badge>
                        )}
                    </Flex>
                </Flex>
                
                <Box className={classes.htmlCompareContainer}>
                    {/* Current Value Column */}
                    <Box className={classes.htmlColumn}>
                        <Box className={`${classes.htmlColumnHeader} ${classes.htmlColumnHeaderCurrent}`}>
                            <Flex align="center" gap="xs">
                                <MaterialSymbol icon="article" size={16} />
                                <span>Valore Attuale</span>
                            </Flex>
                        </Box>
                        <Box className={`${classes.htmlPreview} ${classes.htmlPreviewCurrent}`}>
                            {currentHtml ? (
                                <div dangerouslySetInnerHTML={{ __html: currentHtml }} />
                            ) : (
                                <Box className={classes.emptyHtml}>Nessun contenuto</Box>
                            )}
                        </Box>
                    </Box>

                    {/* Archived Value Column */}
                    <Box className={classes.htmlColumn}>
                        <Box className={`${classes.htmlColumnHeader} ${classes.htmlColumnHeaderArchived}`}>
                            <Flex align="center" gap="xs">
                                <MaterialSymbol icon="inventory_2" size={16} />
                                <span>Valore Archivio</span>
                            </Flex>
                        </Box>
                        <Box className={`${classes.htmlPreview} ${classes.htmlPreviewArchived}`}>
                            {archivedHtml ? (
                                <div dangerouslySetInnerHTML={{ __html: archivedHtml }} />
                            ) : (
                                <Box className={classes.emptyHtml}>Nessun contenuto</Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Stack>
        );
    };

    const renderComparisonTable = (fields: FieldComparison[]) => {
        if (fields.length === 0) {
            return (
                <Flex justify="center" align="center" p="xl">
                    <Text c="dimmed">Nessun campo disponibile per questa sezione</Text>
                </Flex>
            );
        }

        return (
            <ScrollArea>
                <Table striped highlightOnHover withTableBorder className={classes.table}>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th w={50}>
                                <Checkbox
                                    checked={fields.every((f) => fieldSelections[f.key])}
                                    indeterminate={
                                        fields.some((f) => fieldSelections[f.key]) &&
                                        !fields.every((f) => fieldSelections[f.key])
                                    }
                                    onChange={(e) => handleSelectAllInTab(activeTab, e.currentTarget.checked)}
                                    color="teal"
                                />
                            </Table.Th>
                            <Table.Th>Campo</Table.Th>
                            <Table.Th>Valore Attuale</Table.Th>
                            <Table.Th>Valore Archivio</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {fields.map((field) => (
                            <Table.Tr 
                                key={field.key}
                                className={field.isDifferent ? classes.differentRow : undefined}
                            >
                                <Table.Td>
                                    <Checkbox
                                        checked={fieldSelections[field.key] ?? true}
                                        onChange={() => handleFieldToggle(field.key)}
                                        color="teal"
                                    />
                                </Table.Td>
                                <Table.Td>
                                    <Flex align="center" gap="xs">
                                        <Text fw={500} size="sm">{field.label}</Text>
                                        {field.isDifferent && (
                                            <Badge size="xs" color="orange" variant="light">
                                                Diverso
                                            </Badge>
                                        )}
                                    </Flex>
                                </Table.Td>
                                <Table.Td>
                                    <Text size="sm" className={classes.valueCell} lineClamp={2}>
                                        {formatValue(field.currentValue)}
                                    </Text>
                                </Table.Td>
                                <Table.Td>
                                    <Text size="sm" className={classes.valueCell} lineClamp={2} c={field.isDifferent ? "teal.7" : undefined} fw={field.isDifferent ? 500 : undefined}>
                                        {formatValue(field.archivedValue)}
                                    </Text>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </ScrollArea>
        );
    };

    const formatValue = (value: any): string => {
        if (value === undefined || value === null || value === "") {
            return "-";
        }
        if (typeof value === "string" && value.length > 100) {
            return value.substring(0, 100) + "...";
        }
        return String(value);
    };

    const isHtmlTab = (tabKey: CompareTabKey): boolean => {
        return tabKey === "technicalDetails" || tabKey === "description";
    };

    const renderContent = () => {
        if (isLoading) {
            return (
                <Flex justify="center" align="center" h={400}>
                    <Stack align="center" gap="md">
                        <Loader size="lg" />
                        <Text c="dimmed">Caricamento dati dall'archivio...</Text>
                    </Stack>
                </Flex>
            );
        }

        if (error) {
            return (
                <Flex justify="center" align="center" h={400}>
                    <Stack align="center" gap="md">
                        <ThemeIcon size="xl" color="red" variant="light">
                            <MaterialSymbol icon="error" size={32} />
                        </ThemeIcon>
                        <Text c="red">{error}</Text>
                    </Stack>
                </Flex>
            );
        }

        return (
            <Tabs
                value={activeTab}
                onChange={(v) => setActiveTab(v as CompareTabKey)}
                orientation={isMobile ? "horizontal" : "vertical"}
                classNames={{
                    root: classes.tabsRoot,
                    list: isMobile ? classes.tabsListHorizontal : classes.tabsListVertical,
                    panel: classes.tabsPanel,
                    tab: classes.tab,
                }}
            >
                <Tabs.List>
                    {COMPARE_TAB_CONFIGS.map((tab) => (
                        <Tabs.Tab
                            key={tab.key}
                            value={tab.key}
                            leftSection={<MaterialSymbol icon={tab.icon as any} size={18} />}
                            rightSection={
                                getTabDiffCount(tab.key) > 0 ? (
                                    <Badge size="xs" color="yellow" variant="filled" circle>
                                        {getTabDiffCount(tab.key)}
                                    </Badge>
                                ) : null
                            }
                        >
                            {tab.label}
                        </Tabs.Tab>
                    ))}
                </Tabs.List>

                {COMPARE_TAB_CONFIGS.map((tab) => (
                    <Tabs.Panel key={tab.key} value={tab.key}>
                        {isHtmlTab(tab.key) 
                            ? renderHtmlComparison(comparisons[tab.key])
                            : renderComparisonTable(comparisons[tab.key])
                        }
                    </Tabs.Panel>
                ))}
            </Tabs>
        );
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={
                <Flex align="center" gap="sm">
                    <ThemeIcon variant="light" color="teal" size="lg">
                        <MaterialSymbol icon="compare_arrows" size={24} />
                    </ThemeIcon>
                    <Text fw={600} size="lg">Recupera Dati da Archivio</Text>
                </Flex>
            }
            size={isMobile ? "100%" : "80%"}
            centered
            scrollAreaComponent={ScrollArea.Autosize}
            classNames={{
                body: classes.modalBody,
                content: classes.modalContent,
            }}
        >
            <Stack gap="md">
                <Flex 
                    justify="space-between" 
                    align="center" 
                    className={classes.infoBar}
                    wrap="wrap"
                    gap="sm"
                >
                    <Text size="sm" c="dimmed">
                        SKU: <strong>{currentArticle.headerArticleDetails.code}</strong>
                    </Text>
                    <Badge variant="light" color="teal" size="lg">
                        {getSelectedCount()} campi selezionati
                    </Badge>
                </Flex>

                <Box className={classes.contentContainer}>
                    {renderContent()}
                </Box>

                <Flex justify="flex-end" gap="md" className={classes.footer}>
                    <Button
                        variant="subtle"
                        color="gray"
                        radius="lg"
                        onClick={onClose}
                    >
                        Annulla
                    </Button>
                    <Button
                        variant="filled"
                        color="teal"
                        radius="lg"
                        onClick={handleConfirm}
                        disabled={isLoading || !!error || getSelectedCount() === 0}
                        leftSection={<MaterialSymbol icon="check" size={18} />}
                    >
                        Importa Selezionati
                    </Button>
                </Flex>
            </Stack>
        </Modal>
    );
}
